import { create } from 'zustand'

import { ApiRequestError, api, errorMessage, onSessionLost } from '@/lib/api'
import type { PermissionKey } from '@/lib/permissions'
import { Permission } from '@/lib/permissions'
import type { SessionBrief, SessionUser } from '@/types/api'

interface AuthState {
  user: SessionUser | null
  permissions: Set<string>
  /** Distinguishes "still checking" from "definitely signed out", so the app
   *  can hold the first paint instead of flashing the login screen. */
  status: 'unknown' | 'authenticated' | 'anonymous'
  error: string | null
  loginError: { code: string; message: string } | null

  bootstrap: () => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  savePreferences: (preferences: Record<string, unknown>) => Promise<void>
  listSessions: () => Promise<SessionBrief[]>
  revokeOtherSessions: () => Promise<number>
  setLoginError: (error: { code: string; message: string } | null) => void

  can: (permission: PermissionKey) => boolean
  canAny: (...permissions: PermissionKey[]) => boolean
  canAll: (...permissions: PermissionKey[]) => boolean
}

function permissionSet(user: SessionUser | null): Set<string> {
  return new Set(user?.permissions ?? [])
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  permissions: new Set(),
  status: 'unknown',
  error: null,
  loginError: null,

  bootstrap: async () => {
    try {
      const user = await api.get<SessionUser>('/auth/me')
      set({ user, permissions: permissionSet(user), status: 'authenticated', error: null })
    } catch (error) {
      // A 401 here is the normal "not signed in" path, not a failure worth
      // surfacing; anything else is worth showing on the login screen.
      const isAuthError = error instanceof ApiRequestError && error.isAuthError
      set({
        user: null,
        permissions: new Set(),
        status: 'anonymous',
        error: isAuthError ? null : errorMessage(error),
      })
    }
  },

  refreshUser: async () => {
    try {
      const user = await api.get<SessionUser>('/auth/me')
      set({ user, permissions: permissionSet(user) })
    } catch {
      /* the interceptor handles session loss; a failed refresh keeps the
         existing user rather than blanking the console mid-task */
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout', undefined, { retryOnExpiry: false })
    } catch {
      /* signing out locally matters more than the server acknowledging it */
    }
    set({ user: null, permissions: new Set(), status: 'anonymous' })
  },

  savePreferences: async (preferences) => {
    const user = await api.patch<SessionUser>('/auth/preferences', preferences)
    set({ user, permissions: permissionSet(user) })
  },

  listSessions: () => api.get<SessionBrief[]>('/auth/sessions'),

  revokeOtherSessions: async () => {
    const result = await api.post<{ message: string; detail?: Record<string, unknown> }>(
      '/auth/sessions/revoke-others',
    )
    const match = /(\d+)/.exec(result.message)
    return match ? Number(match[1]) : 0
  },

  setLoginError: (loginError) => set({ loginError }),

  can: (permission) => {
    const state = get()
    if (state.user?.is_superuser) return true
    return state.permissions.has(permission)
  },

  canAny: (...permissions) => permissions.some((permission) => get().can(permission)),
  canAll: (...permissions) => permissions.every((permission) => get().can(permission)),
}))

/** When the session cannot be recovered, drop straight to the login screen. */
onSessionLost(() => {
  const { status } = useAuthStore.getState()
  if (status === 'authenticated') {
    useAuthStore.setState({
      user: null,
      permissions: new Set(),
      status: 'anonymous',
      loginError: { code: 'session_expired', message: 'Your session expired. Sign in again.' },
    })
  }
})

/** Selector hooks — components subscribe to the narrowest slice they need. */
export const useUser = () => useAuthStore((state) => state.user)
export const useAuthStatus = () => useAuthStore((state) => state.status)

export function useCan(permission: PermissionKey): boolean {
  return useAuthStore((state) =>
    state.user?.is_superuser ? true : state.permissions.has(permission),
  )
}

export function useCanAny(...permissions: PermissionKey[]): boolean {
  return useAuthStore((state) => {
    if (state.user?.is_superuser) return true
    return permissions.some((permission) => state.permissions.has(permission))
  })
}

export { Permission }
